import type { Task } from './TaskListMolecule.types';

/** The fields of a `/tasks` page that paging depends on; callers keep their own response type. */
export interface TaskPageResponse {
  success?: boolean;
  tasks?: Task[];
  error?: string;
  pagination?: { hasMore?: boolean };
}

export interface TaskPages<P extends TaskPageResponse> {
  /** Every page read, in order. Page-level metadata (counts, groups) is the caller's to pick. */
  pages: P[];
  /** The rows of every page, in order. */
  tasks: Task[];
  /** The offset the next `fetchMore` reads from. */
  nextOffset: number;
  /** Whether the server has rows after the last page read. */
  hasMore: boolean;
}

/**
 * Reads one `/tasks` page from `startOffset`, then further pages until at least `minRows` rows are
 * read or the server has no more. A next page asks for no rows beyond the first page (0). A re-read of
 * a list that has loaded several pages asks for the rows it holds, so it shows the same pages again
 * instead of cutting the list back to the first; the routes cap a page at 100 rows, so a long list
 * takes several requests. Throws on the first page that fails, so the caller applies all of the pages
 * or none of them.
 */
export async function readTaskPages<P extends TaskPageResponse>(
  readPage: (offset: number) => Promise<P>,
  startOffset: number,
  minRows: number
): Promise<TaskPages<P>> {
  const pages: P[] = [];
  const tasks: Task[] = [];
  let hasMore = false;
  do {
    const page = await readPage(startOffset + tasks.length);
    if (!page.success || !page.tasks) {
      throw new Error(page.error || 'Failed to fetch tasks');
    }
    pages.push(page);
    tasks.push(...page.tasks);
    hasMore = page.pagination?.hasMore ?? false;
    if (page.tasks.length === 0) break;
  } while (hasMore && tasks.length < minRows);

  return { pages, tasks, nextOffset: startOffset + tasks.length, hasMore };
}
